import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCommandCenter } from "@/lib/api";
import {
  Sparkles, Sun, AlertOctagon, CalendarClock, ArrowRight, Trophy, Loader2,
} from "lucide-react";

const CARDS = [
  {
    key: "what_happened",
    title: "What happened",
    subtitle: "Recent milestones",
    Icon: Sparkles,
    tint: "bg-[var(--fcc-emma)] text-[var(--fcc-green)]",
    testid: "cmd-card-happened",
  },
  {
    key: "whats_happening",
    title: "What's happening",
    subtitle: "Today",
    Icon: Sun,
    tint: "bg-[var(--fcc-noah)] text-amber-700",
    testid: "cmd-card-happening",
  },
  {
    key: "needs_attention",
    title: "Needs attention",
    subtitle: "Action items",
    Icon: AlertOctagon,
    tint: "bg-rose-50 text-rose-700",
    testid: "cmd-card-attention",
  },
  {
    key: "whats_coming",
    title: "What's coming",
    subtitle: "Next 7 days",
    Icon: CalendarClock,
    tint: "bg-[var(--fcc-green-soft)] text-[var(--fcc-green)]",
    testid: "cmd-card-coming",
  },
];

export default function ParentCommandCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetchCommandCenter().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="fcc-card p-8 text-center text-[var(--fcc-muted)]" data-testid="command-center-loading">
        <Loader2 className="w-5 h-5 mx-auto animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <section data-testid="parent-command-center" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="label-overline">Parent command center</p>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">Your family at a glance</h2>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map((c) => (
          <CommandCard key={c.key} card={c} block={data[c.key]} />
        ))}
      </div>
    </section>
  );
}

function CommandCard({ card, block }) {
  const { Icon } = card;
  const summary = buildSummary(card.key, block);
  return (
    <Link
      to={block?.deeplink || "/app"}
      data-testid={card.testid}
      className="fcc-card p-5 flex flex-col gap-3 hover:bg-[var(--fcc-cream-2)] transition group h-full"
    >
      <div className="flex items-center gap-2">
        <span className={`inline-flex w-9 h-9 rounded-xl items-center justify-center ${card.tint}`}>
          <Icon className="w-4 h-4" />
        </span>
        <div>
          <p className="label-overline">{card.subtitle}</p>
          <p className="font-display text-lg text-[var(--fcc-ink)]">{card.title}</p>
        </div>
      </div>
      <div className="flex-1">
        {summary.bigNumber !== undefined && (
          <p className="font-display text-3xl text-[var(--fcc-ink)] leading-none mt-1">{summary.bigNumber}</p>
        )}
        {summary.lines.length === 0 ? (
          <p className="text-xs text-[var(--fcc-muted)] mt-2">{summary.empty}</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {summary.lines.slice(0, 3).map((ln, idx) => (
              <li key={idx} className="text-xs text-[var(--fcc-ink-soft)] truncate" data-testid={`${card.testid}-line`}>
                · {ln}
              </li>
            ))}
          </ul>
        )}
      </div>
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--fcc-green)] group-hover:translate-x-0.5 transition">
        {summary.cta} <ArrowRight className="w-3 h-3" />
      </span>
    </Link>
  );
}

function buildSummary(key, block) {
  if (!block) return { lines: [], empty: "Nothing yet.", cta: "Open →" };
  if (key === "what_happened") {
    const lines = (block.milestones || []).map((m) => `${m.label} · ${m.child_name || "family"}`);
    return {
      bigNumber: block.milestones?.length ?? 0,
      lines,
      empty: block.tasks_completed_count
        ? `${block.tasks_completed_count} tasks completed this week`
        : "No new milestones yet.",
      cta: "Open timeline",
    };
  }
  if (key === "whats_happening") {
    const lines = (block.events_today || []).map(
      (e) => `${e.title}${e.location ? ` · ${e.location}` : ""}`,
    );
    return {
      bigNumber: block.count,
      lines,
      empty: "Nothing scheduled today.",
      cta: "Open calendar",
    };
  }
  if (key === "needs_attention") {
    const lines = (block.items || []).map((t) => {
      const due = t.due_date ? ` · due ${t.due_date.slice(5, 10)}` : "";
      return `${t.title}${due}`;
    });
    return {
      bigNumber: block.total,
      lines,
      empty: "All clear.",
      cta: block.overdue_count ? `Open tasks (${block.overdue_count} overdue)` : "Open tasks",
    };
  }
  if (key === "whats_coming") {
    const lines = (block.events || []).map(
      (e) => `${(e.event_date || "").slice(5, 10)} · ${e.title}`,
    );
    return {
      bigNumber: block.count,
      lines,
      empty: "No events scheduled.",
      cta: "Open calendar",
    };
  }
  return { lines: [], empty: "—", cta: "Open →" };
}
